import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { ArrowLeft } from 'lucide-react';

import { BaseImportViewProps } from './import-view-props';

export type ProductImportMode = 'add' | 'update';

interface ViewProductsOptionSelectionProps extends BaseImportViewProps {
  flowTitle: string;
  onNext: (mode: ProductImportMode) => void;
  onBack: () => void;
  disabledUpdate?: boolean;
  products?: DealItemWithVersions[];
}

export const ViewProductsOptionSelection: React.FC<
  ViewProductsOptionSelectionProps
> = ({ flowTitle, onNext, disabledUpdate, onBack, products = [] }) => {
  const { t } = useTranslation();
  const hasProducts = products.length > 0;
  const [selectedMode, setSelectedMode] = useState<ProductImportMode>('add');

  const handleNext = () => {
    onNext(selectedMode);
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={onBack}
            aria-label="Revenir au menu"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>{flowTitle}</DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.products_option_selection.choose_option')}{' '}
          :
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-6">
        <RadioGroup
          value={selectedMode}
          onValueChange={(value) => setSelectedMode(value as ProductImportMode)}
          className="space-y-4"
        >
          <div className="flex items-start space-x-3">
            <RadioGroupItem value="add" id="add" className="mt-1" />
            <div className="space-y-1">
              <Label
                htmlFor="add"
                className="cursor-pointer text-base font-medium"
              >
                {t(
                  'components.import_flow.products_option_selection.add_new_products'
                )}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t(
                  'components.import_flow.products_option_selection.add_new_products_description'
                )}
              </p>
            </div>
          </div>

          {hasProducts && !disabledUpdate ? (
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="update" id="update" className="mt-1" />
              <div className="space-y-1">
                <Label
                  htmlFor="update"
                  className="cursor-pointer text-base font-medium"
                >
                  {t(
                    'components.import_flow.products_option_selection.update_existing_products'
                  )}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'components.import_flow.products_option_selection.update_existing_products_description'
                  )}
                </p>
              </div>
            </div>
          ) : null}
        </RadioGroup>

        <div className="flex justify-end">
          <Button onClick={handleNext}>{t('general.next')}</Button>
        </div>
      </div>
    </>
  );
};
